import {skipToken} from '@reduxjs/toolkit/query';
import {Action, DataStatus} from 'platform/components';
import {
  Box,
  Grid,
  GridItem,
  HStack,
  Icon,
  Show,
  Text,
  ThemeColorPath,
  ThemeIconKey,
  VStack,
} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useEffect, useState} from 'react';

import {filter, head, isNil, isNotNil} from 'ramda';

import {
  useCreateAutotracerPlReportMutation,
  useGetAutotracerPlPreviewQuery,
  useGetVehicleAutotracerPlReportsQuery,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {handleApiError, Widget} from '@dms/shared';
import {CebiaWidgetAlert, CebiaWidgetLogo} from '@dms/teas';

import {
  buildArray,
  composePath,
  openInNewTab,
  suffixTestId,
  TestIdProps,
  useNavigate,
} from 'shared/web';

const POLLING_INTERVAL = 10000;

export interface VehicleHistoryReportWidgetVehicle {
  id: string;
  vin?: string | null;
  make?: string | null;
  modelFamily?: string | null;
}

export interface VehicleHistoryReportWidgetProps extends TestIdProps {
  vehicleId: string;
  vehicle?: VehicleHistoryReportWidgetVehicle | null;
}

type ReportStatusLineTone = 'success' | 'warning' | 'informational';

function ReportStatusLine(props: {
  label: string;
  value: string;
  tone: ReportStatusLineTone;
  icon: ThemeIconKey;
}) {
  const tileBackgroundColor = match(props.tone)
    .returnType<ThemeColorPath>()
    .with('success', () => 'palettes.green.20.100')
    .with('warning', () => 'palettes.orange.20.100')
    .with('informational', () => 'palettes.blue.20.100')
    .exhaustive();

  const iconColor = match(props.tone)
    .returnType<ThemeColorPath>()
    .with('success', () => 'severity.success')
    .with('warning', () => 'severity.warning')
    .with('informational', () => 'severity.informational')
    .exhaustive();

  return (
    <Box backgroundColor="general.white" borderRadius="small" padding={2}>
      <HStack spacing={2} align="center" wrap="nowrap">
        <Box backgroundColor={tileBackgroundColor} borderRadius="small" padding={2}>
          <Icon size={6} value={props.icon} color={iconColor} />
        </Box>

        <VStack>
          <Text size="small" color="secondary">
            {props.label}
          </Text>
          <Text size="base" color="primary">
            {props.value}
          </Text>
        </VStack>
      </HStack>
    </Box>
  );
}

export function VehicleHistoryReportWidget(props: VehicleHistoryReportWidgetProps) {
  const navigate = useNavigate();
  const [pendingOpenReportId, setPendingOpenReportId] = useState<string | null>(null);

  const vehicleId = props.vehicleId;
  const vehicle = props.vehicle;

  const isVehicleValid = Boolean(vehicle?.vin && vehicle?.make && vehicle?.modelFamily);

  const [previewPollingInterval, setPreviewPollingInterval] = useState<number | undefined>();
  const [reportsPollingInterval, setReportsPollingInterval] = useState<number | undefined>();

  const {
    data: preview,
    isLoading: isPreviewLoading,
    isError: isPreviewError,
  } = useGetAutotracerPlPreviewQuery(isNotNil(vehicleId) ? {vehicleId} : skipToken, {
    pollingInterval: previewPollingInterval,
  });

  const {
    data: reports,
    isLoading: isReportsLoading,
    isError: isReportsError,
  } = useGetVehicleAutotracerPlReportsQuery(isNotNil(vehicleId) ? {vehicleId} : skipToken, {
    pollingInterval: reportsPollingInterval,
  });

  const [createReport, {isLoading: isCreating}] = useCreateAutotracerPlReportMutation();

  const latestReport = head(reports ?? []);
  const isLatestReportPending =
    latestReport?.status === 'accepted' || latestReport?.status === 'pending';
  const latestCompletedReport = head(
    filter((report) => report.status === 'completed', reports ?? [])
  );
  const canViewLatestReport =
    isNotNil(latestCompletedReport?.url) || isNotNil(latestCompletedReport?.pdfUrl);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPreviewPollingInterval(preview?.status === 'pending' ? POLLING_INTERVAL : undefined);
  }, [preview?.status]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setReportsPollingInterval(isLatestReportPending ? POLLING_INTERVAL : undefined);
  }, [isLatestReportPending]);

  useEffect(() => {
    if (
      isNil(pendingOpenReportId) ||
      isNil(latestReport) ||
      latestReport.id !== pendingOpenReportId
    ) {
      return;
    }
    const url = latestReport.url ?? latestReport.pdfUrl;
    if (isNotNil(url)) {
      openInNewTab(url);
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPendingOpenReportId(null);
    }
  }, [latestReport, pendingOpenReportId]);

  const handleGetReport = () => {
    createReport({vehicleId})
      .unwrap()
      .then((response) => {
        setPendingOpenReportId(response.id);
      })
      .catch(handleApiError);
  };

  const handleViewReport = () => {
    const url = latestCompletedReport?.url ?? latestCompletedReport?.pdfUrl;
    if (isNotNil(url)) {
      openInNewTab(url);
    }
  };

  return (
    <VStack spacing={2}>
      <Widget.Card data-testid={suffixTestId('vehicleHistoryReportWidget', props)}>
        <Widget.Header
          logo={<CebiaWidgetLogo alt={i18n.t('page.integrations.labels.autotracerPlTitle')} />}
          title={i18n.t('page.integrations.labels.autotracerPlTitle')}
          subtitle={
            <Text size="xSmall" color="secondary">
              {i18n.t('page.integrations.labels.autotracerPlDescription')}
            </Text>
          }
          actions={buildArray<Action>()
            .when(isNotNil(latestCompletedReport), {
              type: 'button',
              variant: 'secondary',
              rightIcon: 'action/launch',
              isDisabled: !canViewLatestReport,
              onClick: handleViewReport,
              title: i18n.t('page.integrations.labels.cebiaAutotracerViewReport'),
              'data-testid': suffixTestId('vehicleHistoryReportWidget-viewReport', props),
            })
            .add({
              type: 'button',
              variant: 'secondary',
              isDisabled: !isVehicleValid,
              isLoading: isCreating || isLatestReportPending,
              onClick: handleGetReport,
              title: i18n.t('page.integrations.labels.cebiaAutotraceGetReport'),
              'data-testid': suffixTestId('vehicleHistoryReportWidget-getReport', props),
            })}
          // TODO T20-115202 Define what headerActions "Learn more" link should do
          // headerActions={<Button variant="link" size="small" title={i18n.t('page.integrations.labels.autotracerPlLearnMore')} onClick={() => openInNewTab('#')} />}
          data-testid={suffixTestId('vehicleHistoryReportWidget', props)}
        />
        <DataStatus
          isLoading={isPreviewLoading || isReportsLoading}
          isError={isPreviewError || isReportsError}
        >
          <Widget.Content data-testid={suffixTestId('vehicleHistoryReportWidget', props)}>
            <Grid columns={[1, 2]} spacing={2}>
              <GridItem>
                <ReportStatusLine
                  icon="specification/speedometer"
                  label={i18n.t('page.integrations.labels.autotracerPlMileage')}
                  value={
                    preview?.isMileageAvailable
                      ? i18n.t('page.integrations.labels.autotracerPlAvailable')
                      : i18n.t('page.integrations.labels.autotracerPlNotAvailable')
                  }
                  tone={preview?.isMileageAvailable ? 'success' : 'informational'}
                />
              </GridItem>
              <GridItem>
                <ReportStatusLine
                  icon="automotive/car_star_shining"
                  label={i18n.t('page.integrations.labels.autotracerPlDamage')}
                  value={
                    preview?.isDamagesAvailable
                      ? i18n.t('page.integrations.labels.autotracerPlRecordFound')
                      : i18n.t('page.integrations.labels.autotracerPlNoRecord')
                  }
                  tone={preview?.isDamagesAvailable ? 'warning' : 'success'}
                />
              </GridItem>
            </Grid>
          </Widget.Content>
        </DataStatus>
      </Widget.Card>
      <Show when={!isVehicleValid}>
        <CebiaWidgetAlert
          colorScheme="warning"
          message={i18n.t('page.integrations.labels.cebiaAutotraceRequiredFields')}
          actionText={i18n.t('entity.vehicle.labels.editVehicle')}
          actionCallback={
            vehicle?.id
              ? () => navigate(composePath(vehiclesRoutes.editV2, {params: {id: vehicle.id}}))
              : undefined
          }
        />
      </Show>
    </VStack>
  );
}
